import { useState } from 'react'

import { Download } from 'lucide-react'

interface ExportButtonProps {
  onExport: () => Promise<void>
  disabled?: boolean
  /** Render for a navy header: translucent white surface instead of the solid navy fill. */
  onDark?: boolean
}

/**
 * Imperative export action — NOT a TanStack query (one-shot side effect, not cached state).
 * Surfaces its own failure inline because the report's data fetch suppresses the global toast.
 * A 413 (issues export row cap) gets a tailored "narrow the filters" message.
 */
export function ExportButton({ onExport, disabled, onDark = false }: ExportButtonProps) {
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle')
  const [message, setMessage] = useState<string | null>(null)

  async function handleClick() {
    setState('loading')
    setMessage(null)
    try {
      await onExport()
      setState('idle')
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      setState('error')
      if (status === 413) {
        setMessage('Too many rows — narrow the filters and try again.')
      } else if (status === 400) {
        // Server rejected the export params (e.g. a status group it doesn't accept yet) —
        // retrying won't help, so don't tell the user to. Not a transient failure.
        setMessage("This export isn't available for the current view.")
      } else if (status === 401 || status === 403) {
        setMessage('Your session has expired — please sign in again.')
      } else {
        setMessage('Export failed. Try again.')
      }
    }
  }

  return (
    <span className="inline-flex items-center gap-2">
      {state === 'error' && message && (
        <span className={`text-[11px] ${onDark ? 'text-red-200' : 'text-gis-red'}`}>{message}</span>
      )}
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || state === 'loading'}
        className={`inline-flex items-center gap-[6px] rounded-[10px] px-[12px] py-[6px] text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50 ${
          onDark
            ? 'border border-white/20 bg-white/15 transition-colors hover:bg-white/25'
            : 'bg-gis-navy transition-opacity hover:opacity-90'
        }`}
      >
        <Download className="h-3.5 w-3.5" />
        {state === 'loading' ? 'Exporting…' : 'Export'}
      </button>
    </span>
  )
}
